import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import {
  NotificationService,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

const messages = defineMessages({
  productImportCompleteTitle: {
    id: 'asjT5g',
    defaultMessage: 'Product import complete',
    description: 'Title for product import completion notification',
  },
  productImportCompleteContent: {
    id: 'v68rjW',
    defaultMessage: 'Successfully imported {importedProducts} of {loadedProducts} products.',
    description: 'Content for product import completion notification',
  },
  productImportErrorTitle: {
    id: 'TVnZ32',
    defaultMessage: 'Product import failed',
    description: 'Title for product import error notification',
  },
  inventoryImportErrorTitle: {
    id: '9zp+6Z',
    defaultMessage: 'Inventory import failed',
    description: 'Title for inventory import error notification',
  },
  productImportErrorContent: {
    id: 'kYWFIV',
    defaultMessage: 'There was an error importing your products. Please try again.',
    description: 'Content for product import error notification',
  },
  inventoryImportErrorContent: {
    id: '0j5Bhh',
    defaultMessage: 'There was an error importing your inventory. Please try again.',
    description: 'Content for inventory import error notification',
  },
  stockImportCompleteTitle: {
    id: 'xWfgTQ',
    defaultMessage: 'Stock import complete',
    description: 'Title for stock import completion notification',
  },
  stockImportCompleteContent: {
    id: 'ofLMsw',
    defaultMessage: 'Successfully imported {processedCount} stock levels.',
    description: 'Content for stock import completion notification',
  },
});

/**
 * Service to handle product and inventory import notifications from Pusher events
 */
class ProductImportNotificationServiceClass {
  /**
   * Processes the refresh-store-products pusher message and shows appropriate notifications
   * @param {object} messageData - The pusher message data
   * @param {string} messageData.messageGuid - Unique message identifier
   * @param {object} messageData.data - Import results data
   * @param {number} messageData.data.loadedProducts - Total products processed
   * @param {number} messageData.data.importedProducts - Successfully imported products
   * @param {number} messageData.data.failedImports - Failed imports count
   * @param {string} messageData.uniqueMessageId - Unique message identifier
   */
  processProductImportMessage(messageData) {
    const { data } = messageData;

    if (!data) {
      return;
    }

    if (data.errorMessage) {
      this.showImportErrorNotification();
      return;
    }

    const { loadedProducts, importedProducts, failedImports } = data;

    if (failedImports > 0 && importedProducts === 0) {
      this.showImportErrorNotification();
    } else {
      this.showImportSuccessNotification(importedProducts, loadedProducts);
    }
  }

  /**
   * Shows success notification for completed product import
   * @param {number} importedProducts - Number of successfully imported products
   * @param {number} loadedProducts - Total number of products processed
   */
  showImportSuccessNotification(importedProducts, loadedProducts) {
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.productImportCompleteTitle} />,
      content: <FormattedMessage {...messages.productImportCompleteContent} values={{ importedProducts, loadedProducts }} />,
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }

  /**
   * Shows error notification for failed product import
   */
  showImportErrorNotification() {
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.productImportErrorTitle} />,
      content: <FormattedMessage {...messages.productImportErrorContent} />,
      notificationType: NOTIFICATION_TYPE_ERROR,
      duration: NOTIFICATION_DURATIONS.LONG,
    });
  }

  /**
   * Processes the inventory-action-complete pusher message and shows appropriate notifications
   * @param {object} messageData - The pusher message data
   * @param {object} messageData.data - Import results data
   * @param {number} messageData.data.processedCount - Number of successfully imported stock levels
   */
  processInventoryImportMessage(messageData) {
    if (!messageData) {
      return;
    }

    const { data } = messageData;

    if (!data) {
      return;
    }

    if (data.errorMessage) {
      this.showInventoryImportErrorNotification();
      return;
    }

    const { processedCount } = data;
    this.showInventoryImportSuccessNotification(processedCount);
  }

  /**
   * Shows success notification for completed inventory import
   * @param {number} processedCount - Number of successfully imported stock levels
   */
  showInventoryImportSuccessNotification(processedCount) {
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.stockImportCompleteTitle} />,
      content: <FormattedMessage {...messages.stockImportCompleteContent} values={{ processedCount }} />,
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }

  /**
   * Shows error notification for failed inventory import
   */
  showInventoryImportErrorNotification() {
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.inventoryImportErrorTitle} />,
      content: <FormattedMessage {...messages.inventoryImportErrorContent} />,
      notificationType: NOTIFICATION_TYPE_ERROR,
      duration: NOTIFICATION_DURATIONS.LONG,
    });
  }
}

export const ProductImportNotificationService = new ProductImportNotificationServiceClass();
